// Which machine can take a product, and how many hours a click gives it.
//
// Pure functions over plain data, no React, beside coverage.ts and for the same
// reason: this is where a click decides to consume part of a machine's turno,
// and that arithmetic should be readable and testable on its own rather than
// only reachable by clicking. It used to live inline in production-planning.tsx,
// which is ~1,700 lines.
//
// Everything here works in KILOS and HOURS. Bultos are a display unit derived
// elsewhere; the rate is kg/hr, so hours can only come from kilos.

import type { RateState } from './prediction';
import {
    effectiveKgHr,
    effectiveShiftHours,
    HOURS_INCREMENT,
    HOURS_EPSILON,
    normalizeHours,
    roundHoursUpToIncrement,
} from './plan-helpers';

// The parts of PlanRowState this module needs. Declared structurally (like
// coverage.ts's DemandRow) so the page's state type satisfies it without a cast
// and the tests can build a row from three fields instead of eight.
export interface AllocationRow {
    machine_id: number | null;
    // Null — the normal case — means the row runs the plan's Horas.
    shift_hours: number | null;
    products: { product_id: number | null; hours: number }[];
}

export interface AllocationMachine {
    id: number;
    name: string;
    order_production_type_id?: number | null;
}

export interface AllocationProduct {
    order_production_type_id?: number | null;
}

// A row offered when a product is picked from the drawer, with the facts the
// planner needs to choose between them. EVERY row with a machine becomes one of
// these, including the ones that cannot take the product — a picker that hides
// them cannot answer "why isn't M7 here?".
export interface MachineCandidate {
    index: number;
    machineName: string;
    // The machine's production type matches the product's. When false the row is
    // shown but not selectable: the row's own product picker filters by type, so
    // allowing it would create a state the row editor cannot represent.
    compatible: boolean;
    // kg/hr for THIS product on that machine, and whether it can be trusted yet.
    // `rate` is null while pending as well as when there is no history — the two
    // are told apart by `rateStatus`, and they mean very different things to a
    // planner.
    rate: number | null;
    rateStatus: RateState['status'];
    // Whether `rate` came from this product's own runs on that machine or from
    // the machine's mixed-product average, and how many runs of its own the
    // product has there. A machine average is a materially weaker number — it
    // is systematically biased for this product, since a different calibre and
    // width run at a genuinely different kg/hr — and the picker prints the two
    // identically unless it is told them apart.
    //
    // Deliberately NOT inferable from `producedHere`. That flag answers "has
    // this machine ever run this product", which since the thin-history
    // fallback landed is a DIFFERENT question from "did this product's runs
    // produce this number": a machine that ran it four times answers yes to the
    // first and no to the second.
    rateSource: RateState['source'];
    productRunCount: number;
    freeHours: number;
    hasFreeHours: boolean;
    // The row already makes this product, so choosing it tops up that line
    // rather than adding a second one (the no-duplicate rule).
    alreadyHas: boolean;
    // Hours already given to this product on that row — 0 when it isn't there.
    // Shown next to `alreadyHas` so clicking the same product from a second
    // pedido says how much is already committed, not merely that it is.
    assignedHours: number;
    // Has this machine actually run this product in the last 12 months.
    // `undefined` means the machine's history has not arrived yet — a distinct
    // state from "no", because claiming a machine has never run something on the
    // strength of a query still in flight is a claim we cannot make.
    //
    // Deliberately NOT the same fact as `rateStatus`: a rate needs kilos AND
    // hours captured to resolve, so a machine that ran the product before hourly
    // capture existed reads as 'none' there while being real history here. The
    // planner is asking "who knows how to make this?", not "can we compute it?".
    producedHere: boolean | undefined;
    // Can this row be picked right now. Needs a usable rate on top of the type
    // and hours checks, because the hours a click assigns are derived from it:
    // with no rate there is no honest number to assign (see planClickHours).
    selectable: boolean;
}

// Hours of the row's turno not yet given to a product. Measured against the
// ROW's length (its own override when set, the plan's otherwise) — a machine
// down two hours has six to give, not eight.
export const freeHoursOf = (
    row: AllocationRow,
    planShiftHours: number,
): number =>
    effectiveShiftHours(row.shift_hours, planShiftHours) -
    row.products.reduce((sum, product) => sum + (product.hours || 0), 0);

// Reasons that apply to the WHOLE list, for the drawer's disabled add button.
export const NO_FREE_HOURS_REASON =
    'Ninguna máquina de este tipo tiene horas libres';
export const NO_MACHINE_OF_TYPE_REASON =
    'No hay ningún renglón con una máquina de este tipo de producción';

// EVERY row that has a machine, annotated for this product and sorted into two
// alphabetical groups: machines that have run it before, then the rest.
// Rows that cannot take it are kept rather than filtered out, so the picker can
// show them disabled with a reason. `reason` explains the whole list being
// unusable; it stays null while at least one row is compatible and has hours,
// because then the picker is worth opening even if the rates are still loading.
export function candidateRows({
    rows,
    productId,
    machineById,
    productById,
    planShiftHours,
    rateFor,
    hasProducedProduct,
}: {
    rows: AllocationRow[];
    productId: number;
    machineById: Map<number, AllocationMachine>;
    productById: Map<number, AllocationProduct>;
    planShiftHours: number;
    rateFor: (machineId: number, productId: number) => RateState;
    // Undefined return = this machine's history has not arrived yet. Optional
    // because the ranking does not depend on it: it is shown, not scored.
    hasProducedProduct?: (
        machineId: number,
        productId: number,
    ) => boolean | undefined;
}): { candidates: MachineCandidate[]; reason: string | null } {
    const product = productById.get(productId);
    const typeId = product?.order_production_type_id ?? null;

    const candidates: MachineCandidate[] = rows
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => !!row.machine_id)
        .map(({ row, index }) => {
            const machineId = row.machine_id as number;
            const machine = machineById.get(machineId);
            // Compatible = the machine's production type matches the product's,
            // the same rule the row's own picker uses.
            const compatible =
                !!machine &&
                (typeId === null ||
                    machine.order_production_type_id === typeId);
            const free = normalizeHours(freeHoursOf(row, planShiftHours));
            const hasFreeHours = free >= HOURS_INCREMENT;
            const planned = row.products.find(
                (rowProduct) => rowProduct.product_id === productId,
            );
            const rate = rateFor(machineId, productId);

            return {
                index,
                machineName: machine?.name || '',
                compatible,
                rate: rate.status === 'ready' ? rate.value : null,
                rateStatus: rate.status,
                rateSource: rate.source,
                productRunCount: rate.productRunCount,
                freeHours: free,
                hasFreeHours,
                alreadyHas: !!planned,
                assignedHours: planned?.hours ?? 0,
                producedHere: hasProducedProduct?.(machineId, productId),
                selectable:
                    compatible && hasFreeHours && rate.status === 'ready',
            };
        })
        // Machines that have run this product before, then everything else,
        // alphabetical inside each group. The same two-section shape as the
        // row's product picker, and for the same reason: the planner is
        // scanning for a machine by name, and a name is only findable if its
        // position is stable. The previous order — best-first by rate, free
        // hours and already-planned — reshuffled as rates landed, so the row
        // you were reaching for moved under the cursor.
        //
        // `undefined` (history not loaded yet) sorts with "has not", which
        // means that while nothing has arrived the whole list is simply
        // alphabetical and then settles into two groups.
        .sort((a, b) => {
            const producedA = a.producedHere ? 0 : 1;
            const producedB = b.producedHere ? 0 : 1;
            if (producedA !== producedB) return producedA - producedB;
            // numeric so M2 comes before M10, matching the machine seed order.
            return a.machineName.localeCompare(b.machineName, undefined, {
                numeric: true,
                sensitivity: 'base',
            });
        });

    // The picker is worth opening whenever a row of the right type has hours —
    // even if its rate is still loading, which resolves on its own in a moment.
    const openable = candidates.some(
        (candidate) => candidate.compatible && candidate.hasFreeHours,
    );
    if (openable) return { candidates, reason: null };

    const anyOfType = candidates.some((candidate) => candidate.compatible);
    return {
        candidates,
        reason: anyOfType ? NO_FREE_HOURS_REASON : NO_MACHINE_OF_TYPE_REASON,
    };
}

// Why a click could not be turned into hours.
export type ClickRefusal =
    // the product's demand is already met by stock and the rest of the plan
    | 'nothing-uncovered'
    // the row's turno is fully assigned
    | 'no-free-hours'
    // the machine x product rate has not answered yet
    | 'rate-pending'
    // it answered, and this machine has never run this product
    | 'no-rate';

export const CLICK_REFUSAL_MESSAGE: Record<ClickRefusal, string> = {
    'nothing-uncovered':
        'Este producto ya está cubierto por el inventario y por lo que ya planeaste',
    'no-free-hours': 'Ese renglón ya no tiene horas libres',
    'rate-pending':
        'Todavía se está calculando el rendimiento de este producto en esa máquina',
    // Says what to do instead, because the product can still be planned — just
    // not with hours this page can derive.
    'no-rate':
        'Sin historial de este producto en esa máquina: no se pueden calcular las horas. ' +
        'Agrégalo desde el renglón y escribe las horas tú.',
};

export interface ClickHours {
    // Hours to give this row. 0 when refused.
    assigned: number;
    // Hours the still-uncovered demand would take on this machine.
    needed: number;
    // What this click could not fit — expected, since 54% of pedido lines need
    // more than a whole shift on one machine.
    shortfall: number;
    refusal: ClickRefusal | null;
}

const REFUSED = (refusal: ClickRefusal): ClickHours => ({
    assigned: 0,
    needed: 0,
    shortfall: 0,
    refusal,
});

// How many hours a drawer click gives a row.
//
// Hours come from what is STILL UNMET after inventory: a pedido already covered
// by stock needs no production. The result is capped at the row's free hours, so
// a click can never rewrite an hour the planner set (D8). A result between the
// input's half-hour steps rounds upward when that step still fits, because a
// fractional automatic allocation is not practical on the floor.
//
// A missing rate REFUSES the click rather than falling back to "take the whole
// row" (REV-5). Assigning the entire free block is the one path where one click
// could swallow a machine, and it would be doing so on no evidence at all —
// while the honest alternative, saying so and letting the planner type the
// hours, costs almost nothing: the no-history case is ~2% of production lines.
export function planClickHours({
    uncoveredKilos,
    rate,
    efficiencyPercent,
    freeHours,
}: {
    uncoveredKilos: number;
    rate: RateState;
    // The efficiency the RESULTING line will carry — the existing line's when
    // the click tops one up, the default when it opens a new one. Without it a
    // click would buy hours at the raw historical rate and the line it creates
    // would then predict less than the click promised.
    efficiencyPercent: number;
    freeHours: number;
}): ClickHours {
    const available = normalizeHours(freeHours);
    const incrementedAvailable = normalizeHours(
        Math.floor((available + Number.EPSILON) / HOURS_INCREMENT) *
            HOURS_INCREMENT,
    );
    if (incrementedAvailable <= HOURS_EPSILON) {
        return REFUSED('no-free-hours');
    }
    if (rate.status === 'pending') return REFUSED('rate-pending');
    if (rate.status !== 'ready' || !rate.value || rate.value <= 0) {
        return REFUSED('no-rate');
    }
    if (uncoveredKilos <= 0) return REFUSED('nothing-uncovered');

    const planningRate = effectiveKgHr(rate.value, efficiencyPercent);
    // A zero (or negative) efficiency makes the line produce nothing, so no
    // number of hours covers the demand. Same refusal as a missing rate: there
    // is no honest hour figure to assign.
    if (planningRate <= 0) return REFUSED('no-rate');

    const needed = normalizeHours(uncoveredKilos / planningRate);
    const assigned = normalizeHours(
        Math.min(roundHoursUpToIncrement(needed), incrementedAvailable),
    );
    // The row has free hours (checked above), so a sub-epsilon assignment means
    // the demand itself rounds to nothing. Refusing keeps a zero-hour line out
    // of the plan — the backend rejects those on save, naming a product the
    // planner never knowingly added.
    if (assigned <= HOURS_EPSILON) return REFUSED('nothing-uncovered');

    return {
        assigned,
        needed,
        shortfall: normalizeHours(Math.max(0, needed - assigned)),
        refusal: null,
    };
}

// Put `hours` of a product into a row's product list: topped up when the row
// already runs it, appended otherwise. Merging rather than appending is the
// no-duplicate-product-per-row rule, and it is what makes clicking the same
// product from a second pedido card do the obvious thing — 79% of products are
// wanted by two or more live pedidos at once.
//
// `createLine` is injected because a new line needs a stable React key from a
// counter, and a counter is exactly the kind of state a pure function must not
// own.
export function addProductHours<
    T extends { product_id: number | null; hours: number },
>({
    products,
    productId,
    hours,
    createLine,
}: {
    products: T[];
    productId: number;
    hours: number;
    createLine: (productId: number, hours: number) => T;
}): { products: T[]; merged: boolean } {
    const normalizedHours = normalizeHours(hours);
    const existingIndex = products.findIndex(
        (product) => product.product_id === productId,
    );
    if (existingIndex < 0) {
        return {
            products: [
                ...products.map((product) => ({
                    ...product,
                    hours: normalizeHours(product.hours),
                })),
                createLine(productId, normalizedHours),
            ],
            merged: false,
        };
    }
    return {
        products: products.map((product, index) =>
            index === existingIndex
                ? {
                      ...product,
                      hours: normalizeHours(
                          normalizeHours(product.hours) + normalizedHours,
                      ),
                  }
                : { ...product, hours: normalizeHours(product.hours) },
        ),
        merged: true,
    };
}
